import { useState } from "react";
import type { ProjectCheckoutDeletionPreview } from "@cmmd-forge/contracts";
import type { ProjectGroupRemovalResult } from "~/projectGroupRemovalRunner";
import { settleProjectRemovalRemoved } from "../sidebar/projectRemovalToast";
import { toastManager } from "../ui/toast";

function finishSuccessfulProjectGroupRemoval(input: {
  readonly mode: "checkout" | "project";
  readonly groupKey: string;
  readonly projectName: string;
  readonly results: readonly ProjectGroupRemovalResult[];
  readonly closeDialog: () => void;
  readonly onProjectRemovalComplete: (() => void) | undefined;
}): boolean {
  const completed =
    input.results.length > 0 && input.results.every((result) => result.metadata === "removed");
  if (!completed) return false;

  input.closeDialog();
  if (input.mode === "project") {
    settleProjectRemovalRemoved({
      projectKey: input.groupKey,
      projectName: input.projectName,
    });
    input.onProjectRemovalComplete?.();
    return true;
  }

  const filesDeleted = input.results.some((result) => result.fileDeletion === "deleted");
  toastManager.add({
    type: "success",
    title: "Checkout removed",
    description: filesDeleted ? "Checkout files deleted." : "Files stayed on disk.",
    timeout: 5_000,
    data: { hideCopyButton: true },
  });
  return true;
}

export function useProjectGroupRemovalCompletion(input: {
  readonly groupKey: string;
  readonly projectName: string;
  readonly onProjectRemovalComplete: (() => void) | undefined;
}) {
  const [dialogMode, setDialogMode] = useState<"checkout" | "project" | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [results, setResults] = useState<readonly ProjectGroupRemovalResult[]>([]);
  const [previews, setPreviews] = useState<ReadonlyMap<string, ProjectCheckoutDeletionPreview>>(
    new Map(),
  );
  const closeDialog = () => {
    setResults([]);
    setPreviews(new Map());
    setDialogMode(null);
  };
  const finishRemoval = (
    mode: "checkout" | "project",
    nextResults: readonly ProjectGroupRemovalResult[],
  ) =>
    finishSuccessfulProjectGroupRemoval({
      mode,
      groupKey: input.groupKey,
      projectName: input.projectName,
      results: nextResults,
      closeDialog,
      onProjectRemovalComplete: input.onProjectRemovalComplete,
    });

  return {
    dialogMode,
    setDialogMode,
    isRemoving,
    setIsRemoving,
    isPreviewing,
    setIsPreviewing,
    results,
    setResults,
    previews,
    setPreviews,
    closeDialog,
    finishRemoval,
  };
}
